import { PageConfig } from "@apis:ui_branding/ui_branding";
import { ShineTitle } from "@components:ShineTitle";
import { Box, Stack, Typography } from "@mui/material";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

const getSubtitleText = (
  status: "idle" | "translating" | "success" | "error",
  description: string,
  t: TFunction,
) => {
  switch (status) {
    case "idle":
      return description;
    case "translating":
      return (
        <>
          {t("translation.header.translating")}
          <Box
            component="span"
            sx={{
              display: "inline-block",
              ml: "4px",
            }}
          >
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                component="span"
                sx={{
                  animation: "blink 1.4s infinite both",
                  animationDelay: `${i * 0.2}s`,
                  "@keyframes blink": {
                    "0%, 80%, 100%": { opacity: 0 },
                    "40%": { opacity: 1 },
                  },
                }}
              >
                .
              </Box>
            ))}
          </Box>
        </>
      );
    case "success":
      return t("translation.header.success");
    case "error":
      return t("translation.header.error");
    default:
      return description;
  }
};

interface TranslationHeaderProps {
  status: "idle" | "translating" | "success" | "error";
  pageConfig?: PageConfig | null;
}

export const TranslationHeader = ({
  status,
  pageConfig,
}: TranslationHeaderProps) => {
  const { t } = useTranslation();
  const customTitle = pageConfig?.title;
  const description =
    pageConfig?.description || t("translation.header.defaultDescription");

  return (
    <Stack>
      {customTitle ? (
        <Typography
          sx={{
            fontSize: "32px",
            fontWeight: 700,
            color: (theme) => theme.palette.text.primary,
            textAlign: "center",
          }}
        >
          {customTitle}
        </Typography>
      ) : (
        <ShineTitle
          i18nKey="translation.header.title"
          gradient="linear-gradient(135deg, #D04423 0%, #FF6B4A 40%, #FFC9B8 50%, #FF6B4A 60%, #FF8C6B 100%)"
        />
      )}
      <Typography
        sx={{
          color: (theme) => theme.palette.text.secondary,
          marginTop: "16px",
          fontSize: "16px",
          lineHeight: "24px",
          fontWeight: "400",
          textAlign: "center",
          maxWidth: "600px",
          mx: "auto",
        }}
      >
        {getSubtitleText(status, description, t)}
      </Typography>
    </Stack>
  );
};
